/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import type { HostContext } from '../../@types';
import uuid from 'uuid/v4';
import Request from '../../utils/Request';
import getConfig from '../../config';
import { getAppLocale } from '@intl/adobe-locales';
import FloodgateService from '../FloodgateService';
import { getErrorMessage } from '../../utils/getErrorMessage';
import { getLogger } from '../../utils/SearchLogger';
import PerformanceUtils from '../../utils/PerformanceUtils';

export const authcompletionRequest = async ({
	searchQuery,
	searchConfig,
	searchHostContext,
}: {
	searchQuery: string;
	searchConfig: any;
	searchHostContext: HostContext;
}): Promise<unknown> => {
	try {
		const curRequestId = uuid();
		const startTime = Date.now();
		let sanitizeInput = searchQuery;

		if (typeof String.prototype.normalize === 'function') {
			// normalize() exists, use it
			sanitizeInput = sanitizeInput.normalize();
		}

		sanitizeInput = sanitizeInput.trim().toLocaleLowerCase();
		const env = searchHostContext.environment;
		const locale = getAppLocale('uss', searchHostContext.locale) || 'en_US';
		const hostname = getConfig(env, 'USS_SERVER');
		const scope = searchConfig?.ui?.autoComplete?.scope ?? ['cc_search'];
		const autoCompleteEnvironmentConfig: any = searchConfig?.ui?.autoComplete?.[env] ?? {};
		const searchAutoCompleteCount = autoCompleteEnvironmentConfig?.quickLink?.limit ?? 6;
		const path = '/autocomplete/authcompletions';

		const headers = {
			'Content-Type': 'application/json',
			'x-api-key': searchHostContext.hostInfo.apiKey,
			authorization: `Bearer ${searchHostContext.imsInfo.accessToken}`,
			'x-request-id': curRequestId,
		};
		// Get the list of floodgate campaigns in test
		const floodgateService = new FloodgateService(searchHostContext);
		const floodgateExperiments = floodgateService.getFloodgateCampaignsAndFeatures().campaigns;
		const translatedFloodgateExperiments = [];

		Array.isArray(floodgateExperiments) &&
			floodgateExperiments.forEach((experiment) => {
				translatedFloodgateExperiments.push({
					experimentId: experiment.campaign_id,
					variantId: experiment.variation_id,
					metadata: experiment.metadata,
				});
			});

		let USSContext = {};

		if (searchHostContext.featureFlags.includes('ccsearch_enable_uss_auto_complete_contextual_parameters')) {
			// This is collecting data to pass to USS for Contextual Parameters
			const surface = searchHostContext.hostInfo.surface;
			const client = searchHostContext.hostInfo.client;

			USSContext = {
				application: {
					source: {
						name: client,
						surface,
						platform: client,
					},
				},
			};
		}

		const postBodyData = {
			q: sanitizeInput,
			locale,
			scope,
			text_type: ['query'],
			limit: {
				query_limit: searchAutoCompleteCount,
			},
			context: USSContext,
			experiments: translatedFloodgateExperiments,
		};

		const postBodyJsonData = JSON.stringify(postBodyData);

		const autoCompleteResponse = await Request.withPayload(postBodyJsonData)
			.withHeaders(headers)
			.send('POST', `https://${hostname}${path}`);

		if (autoCompleteResponse.isError()) {
			const loggerData = {
				url: `https://${hostname}${path}`,
				status: autoCompleteResponse.status(),
				message: autoCompleteResponse.statusText(),
				requestId: curRequestId,
				duration: Date.now() - startTime,
				performanceTiming: PerformanceUtils.getPerformanceTimingByName(autoCompleteResponse.requestUrl()),
			};
			getLogger().error('Search Service', 'Problem with Search Auto Suggestion fetch request', loggerData);
			const autocompleteError = new Error('_performSearchAutoCompleteRequest error');
			return autocompleteError;
		}

		return autoCompleteResponse.data();
	} catch (e) {
		getLogger().error('Autocomplete Service', 'Autocomplete Service connect error', getErrorMessage(e));
		const autocompleteError = new Error('Autocomplete Service connect error');
		return autocompleteError;
	}
};
